// QA rubric helpers shared by the QA page and the template editor.

/** A QA rubric's questions: every result_schema field with x-qa, in order (mirrors the backend's qa_rubric.questions). */
export function qaQuestions(resultSchema) {
  const props = (resultSchema && resultSchema.properties) || {};
  // The database re-sorts object keys, so question order comes from `required` (an array keeps its order).
  const required = Array.isArray(resultSchema && resultSchema.required) ? resultSchema.required.filter((k) => k in props) : [];
  const order = [...required, ...Object.keys(props).filter((k) => !required.includes(k))];
  return order.map((k) => [k, props[k]])
    .filter(([, spec]) => spec && typeof spec === 'object' && spec['x-qa'] && typeof spec['x-qa'] === 'object')
    .map(([id, spec]) => {
      const qa = spec['x-qa'];
      return {
        id, question: spec.description || id, section: qa.section || '', points: qa.points ?? 1,
        scored: qa.scored !== false, allow_na: qa.allow_na !== false, auto_fail: !!qa.auto_fail,
      };
    });
}

/** The score of a set of answers: N/A leaves the denominator, info-only questions score nothing, a No on an
 *  auto-fail question flags the call. Mirrors the backend's cm_qa_scoring.compute_score (for previews only; real
 *  scores always come from the backend). */
export function qaScore(questions, answers) {
  const byId = Object.fromEntries((answers || []).map((a) => [a.id, a]));
  let earned = 0;
  let applicable = 0;
  const autoFails = [];
  questions.forEach((q) => {
    const answer = ((byId[q.id] || {}).answer || '').trim();
    if (q.auto_fail && answer === 'No') autoFails.push({ id: q.id, question: q.question });
    if (!q.scored || !['Yes', 'No'].includes(answer)) return;
    applicable += q.points ?? 1;
    if (answer === 'Yes') earned += q.points ?? 1;
  });
  return {
    earned, applicable, failed: autoFails.length > 0, auto_fails: autoFails,
    score_pct: applicable ? Math.round((1000 * earned) / applicable) / 10 : null,
  };
}

const SAMPLE_ANSWERS = ['Yes', 'Yes', 'No', 'Yes', 'N/A'];

/** A made-up grade so a rubric can be previewed as a graded call. Auto-fail questions are answered Yes. */
export function qaSampleGrade(questions) {
  const answers = questions.map((q, i) => {
    const answer = q.auto_fail ? 'Yes' : SAMPLE_ANSWERS[i % SAMPLE_ANSWERS.length];
    return {
      id: q.id, answer,
      quote: answer === 'N/A' ? '' : 'A sample quote from the transcript.',
      rationale: answer === 'N/A' ? 'Sample: not applicable to this call.' : `Sample: why the grader answered ${answer}.`,
    };
  });
  return {
    ...qaScore(questions, answers), answers, source: 'ai',
    summary: 'Sample summary: two or three sentences on how the call went against the rubric.',
    coaching: 'Sample coaching: the most useful thing the care manager could do differently next time.',
  };
}

/** A grade from a result's data, in either shape: per-question fields (current) or an answers list (older AI grades).
 *  Scored with qaScore, for previews of draft tries; the QA page always shows the backend's score. */
export function qaGradeFrom(data, questions) {
  const d = data || {};
  const answers = Array.isArray(d.answers)
    ? d.answers
    : questions.filter((q) => d[q.id] && typeof d[q.id] === 'object')
      .map((q) => ({ id: q.id, answer: d[q.id].answer || '', quote: d[q.id].quote || '', rationale: d[q.id].rationale || '' }));
  return { ...qaScore(questions, answers), answers, summary: d.summary || '', coaching: d.coaching || '', source: 'ai' };
}
